/** A device-bound pseudonymous id. It is the student's identity for a session (the join is
 * idempotent per device) and the per-device event counter's owner. Persisted in localStorage
 * so a refresh keeps the same seat; never contains personal data. */
import { storageKey } from "../product";

const KEY = storageKey("device");

function random(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** ``scope`` separates identities that must never collide inside one browser: the teacher's
 * host device and a student tab opened next to it (demos, testing) each get their own id
 * and therefore their own event counter. */
export function deviceId(scope?: string): string {
  const key = scope ? storageKey(`device.${scope}`) : KEY;
  try {
    const v = localStorage.getItem(key);
    if (v && v.length >= 8) return v;
    const fresh = `d-${random()}`;
    localStorage.setItem(key, fresh);
    return fresh;
  } catch {
    return `d-${random()}`; // a locked-down browser gets a per-load id
  }
}

/** Per-device monotonic sequence for outgoing events; persisted so a reload never reuses one. */
export function nextSeq(scope: string): number {
  const k = storageKey(`seq.${scope}`);
  try {
    const n = Number(localStorage.getItem(k) ?? "0") + 1;
    localStorage.setItem(k, String(n));
    return n;
  } catch {
    return Date.now();
  }
}
